import { Transition } from '@headlessui/react';
import { IconCrop, IconTrash } from '@tabler/icons-react';
import { memo, useMemo, useRef, useCallback, RefObject, useState, useSyncExternalStore } from 'react';
import VideoCropContainer from './VideoCropContainer';
import DebugVideoCropContainer from './DebugVideoCropContainer';
import PlayerMenuTooltip from '@/App/remotion/components/PlayerMenus/PlayerMenuTooltip';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';
import { SelectionOutlineAction } from '@/App/remotion/components/Outlines/types';
import EventBus from '@/libs/eventBus/eventBus';
import { CustomEvents } from '@/libs/eventBus/constants';
import { useRemotionPlayerCurrentTimeFromContext } from '@/App/remotion/MainPlayer/ClipPlayerV2WithNewTimeline/hooks/useRemotionPlayerCurrentTimeFromContext';
import { getSegmentTimeFromPlayerTime } from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/timeUtils';
import { updateClipData } from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/clipUpdateDataUtils';
import { CLIP_UPDATE_TYPE } from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/clipUpdateDataUtils/constants';
import { VideoCropPosition } from '@/player-config/src/types/RemotionPlayerConfigTypes/Scene';
import featureFlagStore from '@/stores/featureFlagStore';
import { isLayoutsWithVisionDataEnabled } from '@/stores/clip';
import { SCREENSHARE_VIDEO_ID } from '@/player-config/src/constants/screenshare';
import { FeatureFlagKeys } from '@/services/featureFlag';
import { getCompositeSegment, getScenesForSegment } from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/playerConfigUtil';
import useAnalytics from '@/hooks/useAnalytics';
import { useNewVideoTagInPreview } from '@/hooks/useNewVideoTagInPreview';

function VideoInCompositeElementMenu({
  videoElementId,
  cropInformation,
  videoElementRef,
  videoAspectRatio,
  videoUrl,
  compositeSegmentId
}: {
  videoElementId: string;
  videoAspectRatio: number;
  cropInformation?: VideoCropPosition;
  videoElementRef: RefObject<HTMLDivElement>;
  videoUrl: string;
  compositeSegmentId: string;
}) {
  const menuRef = useRef<HTMLDivElement>(null);
  const { clipData, fps } = useClipsContext();
  const { currentTime } = useRemotionPlayerCurrentTimeFromContext();
  const [isCropModalOpen, setIsCropModalOpen] = useState(false);
  const [cropImageUrl, setCropImageUrl] = useState<string>('');
  const [containerAspectRatio, setContainerAspectRatio] = useState(1);

  const { useNewVideoTag } = useNewVideoTagInPreview();
  const { selectedVideo, compositeSegment } = useMemo(() => {
    const compositeSegment = getCompositeSegment(clipData, compositeSegmentId);

    // Find the video within that composite segment
    const foundVideo = compositeSegment?.videos?.find(video => video.id === videoElementId);

    return {
      selectedVideo: foundVideo,
      compositeSegment: compositeSegment
    };
  }, [clipData, compositeSegmentId, videoElementId]);

  const currentScene = useMemo(() => {
    // Get the current scenes from the clip

    const scenes = getScenesForSegment(clipData, compositeSegmentId);
    if (!compositeSegment || !scenes) {
      return;
    }

    const segmentTime = getSegmentTimeFromPlayerTime(currentTime, clipData, compositeSegmentId);
    const currentSegmentFrame = segmentTime * fps;

    // Find the current scene at the video's position in time
    return scenes.find(scene => {
      const sceneStartTime = scene.fromFrame;
      const sceneEndTime = scene.toFrame;
      return currentSegmentFrame >= sceneStartTime && currentSegmentFrame <= sceneEndTime;
    });
  }, [clipData, compositeSegment, compositeSegmentId, currentTime, fps]);

  const { trackManualRepositioningPopupOpen } = useAnalytics();

  const handleCrop = useCallback(() => {
    if (videoElementRef.current) {
      const videoElement = videoElementRef.current.querySelector(useNewVideoTag ? 'canvas' : 'video');
      if (videoElement) {
        const canvas = document.createElement('canvas');
        canvas.width = 'videoWidth' in videoElement ? videoElement.videoWidth : videoElement.width;
        canvas.height = 'videoHeight' in videoElement ? videoElement.videoHeight : videoElement.height;
        const canvasContext = canvas.getContext('2d');
        if (canvasContext) {
          canvasContext.drawImage(videoElement, 0, 0);
          const dataURL = canvas.toDataURL('image/png');
          canvas.remove();
          setCropImageUrl(dataURL);
          setContainerAspectRatio(videoElement.clientWidth / videoElement.clientHeight);
          trackManualRepositioningPopupOpen({ clipId: clipData.id, fromRemotionPlayer: true });
          setIsCropModalOpen(true);
        }
      }
    }
  }, [clipData.id, trackManualRepositioningPopupOpen, videoElementRef, useNewVideoTag]);

  const handleDelete = useCallback(async () => {
    updateClipData({
      clipId: clipData.id,
      actionType: CLIP_UPDATE_TYPE.TOGGLE_VIDEO_VISIBILITY_FROM_SCENE,
      compositeId: compositeSegmentId,
      videoId: videoElementId,
      sceneId: currentScene?.id
    });
    EventBus.dispatch(CustomEvents.SelectionOutlineAction, {
      action: SelectionOutlineAction.DESELECT
    });
  }, [clipData.id, compositeSegmentId, currentScene?.id, videoElementId]);

  const featureFlags = useSyncExternalStore(featureFlagStore.subscribe, featureFlagStore.getSnapshot);

  const shouldUseDebugVideoCropContainer = useMemo(() => {
    const isLayoutsEnabled = isLayoutsWithVisionDataEnabled(clipData);
    return (
      featureFlags[FeatureFlagKeys.Use_CL_Show_Person_and_Face_Coordinates_in_Manual_Reposition] && isLayoutsEnabled
    );
  }, [clipData, featureFlags]);

  const shouldMaintainAspectRatioForUploads = useMemo(() => {
    const isScreenshareVideo = videoElementId === SCREENSHARE_VIDEO_ID;
    return !isScreenshareVideo;
  }, [videoElementId]);

  const isLastVideoInScene = useMemo(() => {
    return !currentScene || currentScene.details.visible_video_ids?.length === 1;
  }, [currentScene]);

  if (!selectedVideo || !compositeSegment) {
    return null;
  }

  return (
    <>
      <Transition
        appear
        as="div"
        show
        className="z-[60] flex cursor-auto items-center justify-center transition-all duration-100"
        enterFrom="opacity-0 max-h-0"
        enterTo="opacity-100 max-h-96"
        leaveFrom="opacity-100 max-h-96"
        leaveTo="opacity-0 max-h-0"
      >
        <div
          id="composite-video-element-menu"
          className="relative flex space-x-1 rounded-lg border bg-white p-0.5 shadow"
          onPointerDown={e => e.stopPropagation()}
          onClick={e => e.stopPropagation()}
          ref={menuRef}
        >
          {/* Crop Option */}
          <div className="relative z-10">
            <button
              onClick={handleCrop}
              data-testid="composite-video-element-crop-button"
              className="group/button flex size-10 items-center justify-center rounded-md hover:bg-slate-100"
            >
              <IconCrop size={20} />
              <PlayerMenuTooltip content="Crop" />
            </button>
          </div>

          {/* Delete Option */}
          <div className="relative z-10">
            <button
              onClick={handleDelete}
              data-testid="composite-video-element-delete-button"
              disabled={isLastVideoInScene}
              className="group/button flex size-10 items-center justify-center rounded-md hover:bg-slate-100 disabled:opacity-50"
            >
              <IconTrash size={20} />
              <PlayerMenuTooltip
                content={isLastVideoInScene ? "Can't remove last video from scene" : 'Remove from Scene'}
              />
            </button>
          </div>
        </div>
      </Transition>
      {currentScene &&
        (shouldUseDebugVideoCropContainer ? (
          <DebugVideoCropContainer
            videoUrl={videoUrl}
            compositeSegmentId={compositeSegmentId}
            clipId={clipData.id}
            selectedVideoId={videoElementId}
            isOpen={isCropModalOpen}
            onClose={() => setIsCropModalOpen(false)}
            containerAspectRatio={containerAspectRatio}
            videoAspectRatio={videoAspectRatio}
            maintainAspectRatio={shouldMaintainAspectRatioForUploads}
          />
        ) : (
          <VideoCropContainer
            clipId={clipData.id}
            compositeSegmentId={compositeSegmentId}
            imageUrl={cropImageUrl}
            containerAspectRatio={containerAspectRatio}
            videoElementRef={videoElementRef}
            selectedVideoId={videoElementId}
            scene={currentScene}
            isOpen={isCropModalOpen}
            cropInformation={cropInformation}
            videoAspectRatio={videoAspectRatio}
            onClose={() => setIsCropModalOpen(false)}
            maintainAspectRatio={shouldMaintainAspectRatioForUploads}
          />
        ))}
    </>
  );
}

export default memo(VideoInCompositeElementMenu);
